import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, UtensilsCrossed, Truck, Clock, Sparkles } from "lucide-react";
import { api, money } from "@/lib/api";

const HERO = "/assets/brand/hero-saumon.jpeg";
const FRESH = "https://images.unsplash.com/photo-1591189863430-ab87e120f312?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const SERVICES = [
  {
    title: "Nos mets-préparés",
    desc: "Chaque semaine, découvrez 5 nouveaux mets préparés avec soin par nos chefs. Notre menu se renouvelle sur un cycle de 5 semaines, pour vous offrir variété, fraîcheur et qualité, tout en vous permettant de savourer des repas pratiques et savoureux.",
    image: "/assets/products/escalope-veau.webp",
    href: "/menu", cta: "Voir les mets préparés",
    from: "#1c1917", to: "#2a2320", text: "#F5EFE6", accent: "#B0895A",
  },
  {
    title: "Nos traiteurs",
    desc: "Que ce soit pour un anniversaire, un après-funérailles, la chasse, le temps des Fêtes ou toute autre occasion, notre service traiteur vous propose des buffets froids, chauds, des boîtes à lunchs ainsi que des boîtes repas préparées sur mesure. Nous adaptons notre offre à vos besoins afin de vous offrir une solution simple, savoureuse et de qualité.",
    image: "/assets/products/assiette-pique-nique.webp",
    href: "/buffets", cta: "Ouvrir le Studio Buffet",
    from: "#3a2226", to: "#6E2637", text: "#F7EAEC", accent: "#C9A15B",
  },
  {
    title: "Nos repas classiques",
    desc: "Nos repas classiques sont offerts chaque jour et s'inspirent des recettes de grand-maman, préparées maison avec soin. Des plats simples, réconfortants et savoureux, comme à la maison.",
    image: "/assets/products/spaghetti-viande.webp",
    href: "/menu", cta: "Commander un classique",
    from: "#d9cfbc", to: "#efe8db", text: "#2a2320", accent: "#6E2637",
  },
];

const CAT_IMG = {
  "entrees": "/assets/products/soupe-legumes.webp",
  "plats-principaux": "/assets/products/saumon-moutarde-miel.webp",
  "portion-familiale": "/assets/products/lasagne-viande.webp",
  "produits-maison": "/assets/products/pate-viande.webp",
};

export default function Home() {
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: featured = [] } = useQuery({
    queryKey: ["featured"],
    queryFn: async () => (await api.get("/products", { params: { category: "plats-principaux" } })).data,
  });

  return (
    <div>
      {/* HERO */}
      <section className="dark relative min-h-[86vh] flex items-center overflow-hidden bg-background">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/65 to-ink-900/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/45 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 w-full">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-border text-sm text-coral font-medium mb-6">
              <Sparkles className="h-4 w-4" /> Traiteur artisanal · Portneuf depuis 1978
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground leading-[1.05]">
              Goûtez. Profitez.<br /><span className="text-coral">Savourez.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground font-body max-w-xl leading-relaxed">
              Une cuisine simple, de qualité, préparée avec soin. Commandez vos repas, portions familiales et services traiteur — livrés ou à emporter.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/menu" data-testid="hero-commander" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary hover:opacity-90 text-primary-foreground font-heading font-semibold transition-colors active:scale-95">
                Commander mes repas <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/buffets" data-testid="hero-voir-menus" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full glass border border-border text-foreground font-heading font-semibold hover:bg-foreground/10 transition-colors">
                Studio Buffet
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 -mt-12 relative z-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: UtensilsCrossed, t: "Fait maison", d: "Recettes de grand-maman, préparées sur place chaque jour." },
            { icon: Truck, t: "Livraison le lundi", d: "À emporter ou livré. Minimum 40 $ · livraison 5 $." },
            { icon: Clock, t: "Menu 5 semaines", d: "5 nouveaux mets préparés chaque semaine, en rotation." },
          ].map((f, i) => (
            <div key={i} className="p-6 rounded-2xl bg-ink-800 border border-border animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <f.icon className="h-8 w-8 text-coral mb-3" />
              <h3 className="font-heading font-bold text-foreground">{f.t}</h3>
              <p className="text-sm text-muted-foreground mt-1">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-coral font-heading text-sm uppercase tracking-widest">Notre catalogue</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mt-1">Explorez nos catégories</h2>
          </div>
          <Link to="/menu" className="hidden sm:inline-flex items-center gap-1 text-muted-foreground hover:text-coral transition-colors">Tout voir <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c, i) => (
            <Link
              key={c.id}
              to={`/menu?cat=${c.id}`}
              data-testid={`home-cat-${c.id}`}
              className="group relative h-56 rounded-2xl overflow-hidden border border-border animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <img src={CAT_IMG[c.id]} alt={c.nom} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <h3 className="font-heading text-xl font-bold text-foreground">{c.nom}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NOS SERVICES — bandeau premium dégradé + animé */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-4">
        <div className="mb-8">
          <p className="text-coral font-heading text-sm uppercase tracking-widest">Ce que nous offrons</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mt-1">Nos services</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Link
              key={s.title}
              to={s.href}
              data-testid={`service-${i}`}
              className="group relative rounded-3xl overflow-hidden border border-border shadow-2xl animate-fade-up flex flex-col hover:-translate-y-1 transition-transform"
              style={{ animationDelay: `${i * 140}ms` }}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/25 to-transparent" />
              </div>
              <div
                className="flex-1 p-7 relative"
                style={{ background: `linear-gradient(160deg, ${s.from} 0%, ${s.to} 100%)`, color: s.text }}
              >
                <span className="inline-block h-1 w-10 rounded-full mb-4" style={{ background: s.accent }} />
                <h3 className="font-heading text-2xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: s.text, opacity: 0.85 }}>{s.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-heading text-sm font-semibold" style={{ color: s.accent }}>
                  {s.cta} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED / WEEKLY */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <div className="rounded-3xl bg-ink-800 border border-border overflow-hidden grid lg:grid-cols-2">
          <div className="relative min-h-[300px]">
            <img src={FRESH} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-ink-800 to-transparent" />
          </div>
          <div className="p-8 sm:p-12">
            <p className="text-coral font-heading text-sm uppercase tracking-widest">Nos mets préparés</p>
            <h2 className="font-heading text-3xl font-bold text-foreground mt-2">Découvrez cette semaine</h2>
            <p className="text-muted-foreground mt-3">Une sélection de nos plats principaux préparés avec soin.</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {featured.slice(0, 4).map((p) => (
                <Link key={p.id} to="/menu" className="flex items-center gap-3 p-3 rounded-xl bg-foreground/5 border border-border hover:border-coral/40 transition-colors">
                  <img src={p.image} alt={p.nom} className="h-12 w-12 rounded-lg object-cover" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{p.nom}</p>
                    <p className="text-xs text-coral font-bold">{money(p.prix)}</p>
                  </div>
                </Link>
              ))}
            </div>
            <Link to="/menu" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:opacity-90 text-primary-foreground font-heading font-semibold transition-colors">
              Commander maintenant <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
